import { addDays, format, parseISO } from 'date-fns';
import { clampDateRange, getKstToday } from '@/shared/lib';
import { UI_TEXT } from '@/shared/languages';
import type { FilterFieldSpec } from './filter-schema';
import { periodDefaults, type PeriodType } from './period-math';

/**
 * 기간 필터 값(발전량·수익 조회 공용 shape) — 날짜기준(period) + 단일 기준일(date) + range 시작/종료.
 * 각 화면의 Applied 필터가 이를 확장한다(발전량 = +inverterId). 순수 파생·세터·칩 spec 은 여기 SSOT.
 */
export interface PeriodFilterValue {
  /** 날짜기준 — 일/월/연/기간. */
  period: PeriodType;
  /** 단일(일/월/연) 기준값 yyyy-MM-dd. */
  date: string;
  /** range 시작 yyyy-MM-dd. */
  startDate: string;
  /** range 종료 yyyy-MM-dd. */
  endDate: string;
}

/** range 최대 조회 span(일) — 레거시(`usePeriodSelector maxRangeDays: 30`) 정합. 기본 span·입력 경계·클램프 공용. */
export const PERIOD_FILTER_MAX_RANGE_DAYS = 30;

/** yyyy-MM-dd 를 days 만큼 이동(입력 min/max·span 클램프 공용). */
export function shiftDate(ymd: string, days: number): string {
  return format(addDays(parseISO(ymd), days), 'yyyy-MM-dd');
}

/**
 * 기간(range) 클램프 — 역전 방지(clampDateRange) + 최대 span 보장. 방금 바꾼 쪽(changed)을 존중하고 반대쪽을
 * 이동해 항상 유효 상태(0 ≤ 종료−시작 ≤ N, 종료 ≤ 오늘)를 유지한다. 입력 min/max 가 1차 차단, 이 클램프가 backstop.
 */
export function clampPeriodRange(
  start: string,
  end: string,
  changed: 'start' | 'end',
  maxDays = PERIOD_FILTER_MAX_RANGE_DAYS,
): { start: string; end: string } {
  const { start: s, end: e } = clampDateRange(start, end, changed); // 역전 우선
  if (changed === 'start') {
    const cap = shiftDate(s, maxDays);
    const today = getKstToday();
    const maxEnd = cap < today ? cap : today;
    return { start: s, end: e > maxEnd ? maxEnd : e };
  }
  const minStart = shiftDate(e, -maxDays);
  return { start: s < minStart ? minStart : s, end: e };
}

/**
 * 기간 세터 factory — 발전량·수익 **live** 상태 훅 공유. `setFilters`(functional update)로 write. 날짜기준 변경은
 * 기간(date/start/end)을 단위 기본값으로 cascade 리셋, range 는 clampPeriodRange 로 역전·초과 차단.
 */
export function periodSetters<T extends PeriodFilterValue>(
  setFilters: (updater: (prev: T) => T) => void,
  maxDays = PERIOD_FILTER_MAX_RANGE_DAYS,
) {
  return {
    setPeriod: (period: PeriodType) =>
      setFilters((prev) => ({ ...prev, period, ...periodDefaults(period, maxDays) })),
    setDate: (date: string) => setFilters((prev) => ({ ...prev, date })),
    setStartDate: (startDate: string) =>
      setFilters((prev) => {
        const { start, end } = clampPeriodRange(startDate, prev.endDate, 'start', maxDays);
        return { ...prev, startDate: start, endDate: end };
      }),
    setEndDate: (endDate: string) =>
      setFilters((prev) => {
        const { start, end } = clampPeriodRange(prev.startDate, endDate, 'end', maxDays);
        return { ...prev, startDate: start, endDate: end };
      }),
  };
}

/** 날짜기준(일/월/연/기간) 표시 라벨 — PERIOD_UNIT_* SSOT(조회기준 칩 값·select 옵션 공유). */
const DATE_TYPE_LABEL: Record<PeriodType, string> = {
  day: UI_TEXT.TERM_DAY,
  month: UI_TEXT.TERM_MONTH,
  year: UI_TEXT.TERM_YEAR,
  range: UI_TEXT.TERM_PERIOD,
};

/** 조회기간 칩 값 — 날짜기준별(일=yyyy-MM-dd·월=yyyy-MM·연=yyyy·기간=시작~종료). */
function formatPeriodChip(f: PeriodFilterValue): string {
  switch (f.period) {
    case 'day':
      return f.date;
    case 'month':
      return f.date.slice(0, 7);
    case 'year':
      return f.date.slice(0, 4);
    case 'range':
      return `${f.startDate} ~ ${f.endDate}`;
  }
}

/**
 * 조회조건 기간 칩(발전량·수익 공용) — 조회기준(날짜기준 일/월/연/기간) + 조회기간(값). 둘 다 항상 존재·비제거
 * (× 없음·배지 미집계, admin 조회기간·상태 칩 정합). 화면별 추가 차원(예: 발전량 인버터)은 각 스키마가 concat.
 */
export function periodChipSpecs<T extends PeriodFilterValue>(): FilterFieldSpec<T>[] {
  return [
    {
      key: 'dateType',
      label: UI_TEXT.PERIOD_FILTER_DATE_TYPE,
      chip: (f) => ({ label: `${UI_TEXT.PERIOD_FILTER_DATE_TYPE}: ${DATE_TYPE_LABEL[f.period]}` }),
    },
    {
      key: 'period',
      label: UI_TEXT.PERIOD_FILTER_DATE_DIM('day'),
      chip: (f) => ({
        label: `${UI_TEXT.PERIOD_FILTER_DATE_DIM(f.period)}: ${formatPeriodChip(f)}`,
      }),
    },
  ];
}
